import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import trustService from '@api/v1/trustService'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'

const TrustAccounting = () => {
  useDocumentTitle('Trust Accounting')
  const [selectedAccountId, setSelectedAccountId] = useState(null)

  const accountsQuery = useQuery({
    queryKey: ['trust-accounts'],
    queryFn: () => trustService.getAccounts(),
  })

  const txQuery = useQuery({
    queryKey: ['trust-transactions', selectedAccountId],
    queryFn: () => trustService.getTransactions(selectedAccountId),
    enabled: !!selectedAccountId,
  })

  /*
   * CC-53132 — three outcomes per read, not two. See `@api/v1/readState`.
   *
   * Both panels below used to branch on `isLoading` / `error` / `length === 0`.
   * `isLoading` is `isPending && isFetching`, so it is FALSE for a query that is
   * pending but NOT in flight — above all a PAUSED one (react-query's default
   * `networkMode: 'online'` while `onlineManager` believes the browser is
   * offline). `queryFn` is never called, nothing throws, nothing is logged, and
   * `data` stays `undefined` — which both `accounts` and `transactions`
   * flattened to `[]`. The screen then said **"No trust accounts configured"**,
   * and **"No transactions found"** under a JSON dump of `undefined`, for as
   * long as the browser stayed offline.
   *
   * On a trust ledger that is the worst possible lie. A trust account the firm
   * cannot see is a trust account nobody reconciles, and "no transactions" on a
   * client's trust balance is a statement a bar association would read as fact.
   * There is also no request to find in any log on either side, which is why
   * this class of defect survives server-side investigation.
   *
   * The transactions read was worse still: it never destructured `error` at all,
   * so a *failed* transactions fetch — a 500, an expired session — rendered the
   * same "No transactions found" as a genuinely empty account.
   *
   * `useReadState` rather than the bare `readState` because it carries the
   * permanent blocked-read diagnostic (a paused fetch leaves no other trace).
   *
   * `idle` decision, both queries: FOLDED INTO LOADING.
   *   - `trust-accounts` has no `enabled` option, so it can never be idle.
   *   - `trust-transactions` IS conditionally enabled (`!!selectedAccountId`),
   *     so it is idle whenever no account is picked — but that is precisely the
   *     case the panel already answers with its own "Click a trust account"
   *     prompt, which renders *instead of* these branches. Inside the branch
   *     where an account IS selected the query is always enabled, so `idle`
   *     there can only be the momentary tick before the fetch starts, which is
   *     loading by any honest reading. It is folded in rather than left
   *     unhandled because an unhandled state renders NOTHING, and a blank
   *     transactions panel is the next reader's bug.
   */
  const accountsState = useReadState('trust-accounts', accountsQuery)
  const txState = useReadState(`trust-transactions:${selectedAccountId ?? 'none'}`, txQuery)

  // Coerced to arrays only so the lists below can be mapped. The predicate that
  // decides whether an EMPTY list may be described to the user as empty is the
  // read state above, never the coerced length — that swap is the whole fix.
  const rawData = accountsQuery.data?.data
  const accounts = Array.isArray(rawData) ? rawData : (rawData?.accounts ?? [])
  const trustSettings = rawData?.globaltrustSettings
  const txRaw = txQuery.data?.data
  const transactions = Array.isArray(txRaw) ? txRaw : (txRaw?.transactions ?? txRaw?.Transactions ?? [])

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-display text-gray-900">Trust Accounting</h1>
        <p className="text-sm text-gray-500 mt-1">Manage trust accounts, deposits, disbursements, and transfers</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Trust Accounts List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 lg:sticky lg:top-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto" role="region" aria-label="Trust accounts">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Trust Accounts</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {(accountsState === 'loading' || accountsState === 'idle') && (
              <div className="p-8 flex justify-center"><LoadingSpinner message="Loading accounts..." /></div>
            )}

            {/* Paused: no request was sent, so nothing about this firm's trust
                accounts is known. A spinner here would be almost as dishonest as
                the empty state — it would sit forever, because a paused query
                does not resolve on its own. `role="alert"` because the user is
                looking at a panel that previously read as a settled fact about
                their client money. */}
            {accountsState === 'blocked' && (
              <div role="alert" className="m-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <p className="font-medium">Can’t reach the server, so we haven’t loaded your trust accounts.</p>
                <p className="mt-1 text-amber-800">
                  This is not a firm with no trust accounts — the list was never requested. Your
                  connection looks offline; check it and try again.
                </p>
                <button
                  type="button"
                  onClick={() => accountsQuery.refetch()}
                  className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Failed. The sentence itself is unchanged — it was already correct
                and already announced — but it now says what it is NOT, and it
                offers a way out instead of leaving a reload as the only move. */}
            {accountsState === 'failed' && (
              <div role="alert" className="m-5 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
                <p className="font-medium">Failed to load trust accounts</p>
                <p className="mt-1">
                  This is not a firm with no trust accounts — nothing was read, so nothing here
                  describes your data.
                </p>
                <button
                  type="button"
                  onClick={() => accountsQuery.refetch()}
                  className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Only now — a completed read that returned nothing — is "no trust
                accounts configured" a true sentence. */}
            {accountsState === 'ready' && accounts.length === 0 && (
              <div className="p-5">
                <p className="text-sm text-gray-500 text-center mb-3">No trust accounts configured</p>
                {trustSettings && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase">Global Trust Settings</p>
                    <p className="text-sm text-gray-700">Min Balance: ${trustSettings.GlobalMinimumBal?.toLocaleString()}</p>
                    <p className="text-sm text-gray-700">Replenishment Level: ${trustSettings.GlobalReplenishmentLevel?.toLocaleString()}</p>
                  </div>
                )}
              </div>
            )}

            {accountsState === 'ready' && accounts.map((acct, i) => {
              const id = acct.TrustAccountId || acct.Id || acct.id || i
              const name = acct.AccountName || acct.Name || acct.name || `Account ${i + 1}`
              const balance = acct.Balance ?? acct.balance
              return (
                <button
                  key={id}
                  onClick={() => setSelectedAccountId(id)}
                  className={`w-full text-left px-5 py-3 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-cb-accent/30 ${
                    selectedAccountId === id ? 'bg-cb-light border-l-2 border-cb-accent' : ''
                  }`}
                  aria-selected={selectedAccountId === id}
                >
                  <p className="text-sm font-medium text-gray-900">{name}</p>
                  {balance != null && (
                    <p className="text-xs text-gray-500 mt-0.5">Balance: ${Number(balance).toFixed(2)}</p>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Transactions */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100" role="region" aria-label="Trust transactions">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">
              {selectedAccountId ? 'Transactions' : 'Select a Trust Account'}
            </h2>
          </div>
          {!selectedAccountId ? (
            /* The page's OWN "not asked yet", and the one honest absence on this
               screen: the user has genuinely not picked an account. It stays
               ahead of the state branches so the disabled query's `idle` never
               reaches them. */
            <p className="p-8 text-sm text-gray-500 text-center">Click a trust account to view transactions</p>
          ) : (
            <>
              {(txState === 'loading' || txState === 'idle') && (
                <div className="p-8 flex justify-center"><LoadingSpinner message="Loading transactions..." /></div>
              )}

              {txState === 'blocked' && (
                <div role="alert" className="m-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-medium">Can’t reach the server, so we haven’t loaded this account’s transactions.</p>
                  <p className="mt-1 text-amber-800">
                    This is not an account with no activity — no request was sent, and no balance
                    shown here has been checked against the ledger. Your connection looks offline;
                    check it and try again.
                  </p>
                  <button
                    type="button"
                    onClick={() => txQuery.refetch()}
                    className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  >
                    Try again
                  </button>
                </div>
              )}

              {/* This branch had no render site at all before: the transactions
                  query never destructured `error`, so a 500 or an expired
                  session landed on "No transactions found" — a failed read
                  wearing an empty account's clothes. */}
              {txState === 'failed' && (
                <div role="alert" className="m-5 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
                  <p className="font-medium">Failed to load transactions for this trust account</p>
                  <p className="mt-1">
                    This is not an account with no activity — the ledger was never read.
                  </p>
                  <button
                    type="button"
                    onClick={() => txQuery.refetch()}
                    className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                  >
                    Try again
                  </button>
                </div>
              )}

              {txState === 'ready' && transactions.length === 0 && (
                <div className="p-5">
                  <p className="text-sm text-gray-500 text-center py-4">No transactions found</p>
                  <pre className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600 overflow-x-auto max-h-48">
                    {JSON.stringify(txRaw, null, 2)}
                  </pre>
                </div>
              )}

              {txState === 'ready' && transactions.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" role="table">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                        <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                        <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Matter</th>
                        <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Notes</th>
                        <th scope="col" className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {transactions.map((tx, i) => (
                        <tr key={tx.Id || tx.TransactionId || i} className="hover:bg-gray-50/50">
                          <td className="px-5 py-3 text-gray-600">{tx.Date || tx.TransactionDate || '—'}</td>
                          <td className="px-5 py-3 text-gray-900">{tx.Type || tx.TransactionType || '—'}</td>
                          <td className="px-5 py-3 text-gray-600">{tx.MatterName || tx.Matter || '—'}</td>
                          <td className="px-5 py-3 text-gray-600 max-w-xs truncate">{tx.Notes || tx.Description || '—'}</td>
                          <td className="px-5 py-3 text-right font-medium text-gray-900">
                            ${Number(tx.Amount ?? tx.amount ?? 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default TrustAccounting
